import {
  combineCounts,
  formatFormula,
  WATER,
  type ChoiceQuestion,
  type ElementCounts,
  type FormulaSegment,
} from './polymersBiomolecules';

// Biomolecule data for the Polymers & Biomolecules Lab. Shares the data meta, sources and review
// status declared in `POLYMER_DATA_META` (polymersBiomolecules.ts).

// ---------------------------------------------------------------------------------------------
// Biomolecules: amino acids and peptides
// ---------------------------------------------------------------------------------------------

export type SideChainClass = 'non-polar' | 'polar' | 'acidic' | 'basic';

export interface AminoAcid {
  id: string;
  name: string;
  code: string;
  /** R group as written on the α-carbon. */
  rGroup: string;
  rGroupCounts: ElementCounts;
  /** Reference molecular formula, checked against backbone + R group in tests. */
  formula: ElementCounts;
  sideChainClass: SideChainClass;
  note: string;
}

/** H₂N–CH–COOH without the R group. */
export const AMINO_ACID_BACKBONE: ElementCounts = { C: 2, H: 4, N: 1, O: 2 };

export const AMINO_ACIDS: AminoAcid[] = [
  {
    id: 'gly',
    name: 'glycine',
    code: 'Gly',
    rGroup: 'H',
    rGroupCounts: { H: 1 },
    formula: { C: 2, H: 5, N: 1, O: 2 },
    sideChainClass: 'non-polar',
    note: 'The smallest R group: a single hydrogen atom.',
  },
  {
    id: 'ala',
    name: 'alanine',
    code: 'Ala',
    rGroup: 'CH₃',
    rGroupCounts: { C: 1, H: 3 },
    formula: { C: 3, H: 7, N: 1, O: 2 },
    sideChainClass: 'non-polar',
    note: 'A small hydrocarbon R group that takes part in dispersion forces.',
  },
  {
    id: 'ser',
    name: 'serine',
    code: 'Ser',
    rGroup: 'CH₂OH',
    rGroupCounts: { C: 1, H: 3, O: 1 },
    formula: { C: 3, H: 7, N: 1, O: 3 },
    sideChainClass: 'polar',
    note: 'The –OH in the R group can form hydrogen bonds.',
  },
  {
    id: 'cys',
    name: 'cysteine',
    code: 'Cys',
    rGroup: 'CH₂SH',
    rGroupCounts: { C: 1, H: 3, S: 1 },
    formula: { C: 3, H: 7, N: 1, O: 2, S: 1 },
    sideChainClass: 'polar',
    note: 'Two –SH groups can join as a covalent disulfide bridge (–S–S–).',
  },
  {
    id: 'lys',
    name: 'lysine',
    code: 'Lys',
    rGroup: 'CH₂CH₂CH₂CH₂NH₂',
    rGroupCounts: { C: 4, H: 10, N: 1 },
    formula: { C: 6, H: 14, N: 2, O: 2 },
    sideChainClass: 'basic',
    note: 'The –NH₂ in the R group can gain H⁺ to become –NH₃⁺ and form ionic interactions.',
  },
  {
    id: 'asp',
    name: 'aspartic acid',
    code: 'Asp',
    rGroup: 'CH₂COOH',
    rGroupCounts: { C: 2, H: 3, O: 2 },
    formula: { C: 4, H: 7, N: 1, O: 4 },
    sideChainClass: 'acidic',
    note: 'The –COOH in the R group can lose H⁺ to become –COO⁻ and form ionic interactions.',
  },
  {
    id: 'phe',
    name: 'phenylalanine',
    code: 'Phe',
    rGroup: 'CH₂C₆H₅',
    rGroupCounts: { C: 7, H: 7 },
    formula: { C: 9, H: 11, N: 1, O: 2 },
    sideChainClass: 'non-polar',
    note: 'A large non-polar benzene ring that tends to sit inside a folded protein.',
  },
];

export function aminoAcidCounts(acid: AminoAcid): ElementCounts {
  return combineCounts({ counts: AMINO_ACID_BACKBONE }, { counts: acid.rGroupCounts });
}

export const PEPTIDE_MIN_LENGTH = 2;
export const PEPTIDE_MAX_LENGTH = 3;

export interface Peptide {
  sequence: AminoAcid[];
  kind: 'dipeptide' | 'tripeptide';
  name: string;
  segments: FormulaSegment[];
  peptideBonds: number;
  waterReleased: number;
  counts: ElementCounts;
  formula: string;
}

export type PeptideResult = { ok: true; peptide: Peptide } | { ok: false; message: string };

function residueText(acid: AminoAcid): string {
  return acid.id === 'gly' ? 'CH₂' : `CH(${acid.rGroup})`;
}

/** Joins two or three reviewed amino acids from the N-terminus to the C-terminus. */
export function buildPeptide(ids: string[]): PeptideResult {
  if (ids.length < PEPTIDE_MIN_LENGTH || ids.length > PEPTIDE_MAX_LENGTH) {
    return {
      ok: false,
      message: `Choose ${PEPTIDE_MIN_LENGTH} or ${PEPTIDE_MAX_LENGTH} amino acids. This model builds only dipeptides and tripeptides.`,
    };
  }
  const sequence = ids.map((id) => AMINO_ACIDS.find((acid) => acid.id === id) ?? null);
  if (sequence.some((acid) => acid === null)) {
    return { ok: false, message: 'One of those amino acids is not in the reviewed set.' };
  }
  const acids = sequence as AminoAcid[];
  const bonds = acids.length - 1;
  const segments: FormulaSegment[] = [{ text: 'H₂N–' }];
  acids.forEach((acid, index) => {
    segments.push({ text: residueText(acid) });
    if (index < acids.length - 1) {
      segments.push({ text: '–' }, { text: 'CO–NH', link: true }, { text: '–' });
    }
  });
  segments.push({ text: '–COOH' });
  const counts = combineCounts(
    ...acids.map((acid) => ({ counts: acid.formula })),
    { counts: WATER, times: -bonds },
  );
  return {
    ok: true,
    peptide: {
      sequence: acids,
      kind: acids.length === 2 ? 'dipeptide' : 'tripeptide',
      name: acids.map((acid) => acid.code).join('–'),
      segments,
      peptideBonds: bonds,
      waterReleased: bonds,
      counts,
      formula: formatFormula(counts),
    },
  };
}

export interface ProteinLevel {
  id: 'primary' | 'secondary' | 'tertiary' | 'quaternary';
  name: string;
  whatItIs: string;
  heldBy: string;
  example: string;
}

export const PROTEIN_LEVELS: ProteinLevel[] = [
  {
    id: 'primary',
    name: 'Primary',
    whatItIs: 'The sequence of amino acids in one polypeptide chain.',
    heldBy: 'Covalent peptide (amide) bonds between amino acids.',
    example: 'Gly–Ala–Ser… read from the N-terminus to the C-terminus.',
  },
  {
    id: 'secondary',
    name: 'Secondary',
    whatItIs: 'Local folding of the backbone into an α-helix or a β-pleated sheet.',
    heldBy: 'Hydrogen bonds between backbone C=O and N–H groups.',
    example: 'α-helices in keratin (hair).',
  },
  {
    id: 'tertiary',
    name: 'Tertiary',
    whatItIs: 'The overall three-dimensional shape of one polypeptide chain.',
    heldBy:
      'Interactions between R groups: hydrogen bonds, ionic interactions, dispersion forces and covalent disulfide bridges.',
    example: 'The folded shape of an enzyme active site.',
  },
  {
    id: 'quaternary',
    name: 'Quaternary',
    whatItIs: 'Two or more polypeptide chains held together as one protein.',
    heldBy: 'The same R-group interactions as tertiary structure, now between chains.',
    example: 'Haemoglobin: four polypeptide chains.',
  },
];

// ---------------------------------------------------------------------------------------------
// Biomolecules: carbohydrates and lipids
// ---------------------------------------------------------------------------------------------

export const GLUCOSE: ElementCounts = { C: 6, H: 12, O: 6 };
export const FRUCTOSE: ElementCounts = { C: 6, H: 12, O: 6 };

export interface SugarCondensation {
  id: 'maltose' | 'sucrose';
  reactants: [string, string];
  reactantCounts: [ElementCounts, ElementCounts];
  product: string;
  productCounts: ElementCounts;
  linkDetail: string;
}

export const DISACCHARIDES: SugarCondensation[] = [
  {
    id: 'maltose',
    reactants: ['glucose', 'glucose'],
    reactantCounts: [GLUCOSE, GLUCOSE],
    product: 'maltose',
    productCounts: { C: 12, H: 22, O: 11 },
    linkDetail: 'α(1→4) glycosidic link',
  },
  {
    id: 'sucrose',
    reactants: ['glucose', 'fructose'],
    reactantCounts: [GLUCOSE, FRUCTOSE],
    product: 'sucrose',
    productCounts: { C: 12, H: 22, O: 11 },
    linkDetail: 'α(1→2)β glycosidic link between glucose C1 and fructose C2',
  },
];

export interface Polysaccharide {
  id: 'starch' | 'cellulose';
  name: string;
  monomer: string;
  link: string;
  role: string;
  /** Glucose residue in the chain: C₆H₁₂O₆ − H₂O. */
  residueCounts: ElementCounts;
}

const GLUCOSE_RESIDUE = combineCounts({ counts: GLUCOSE }, { counts: WATER, times: -1 });

export const POLYSACCHARIDES: Polysaccharide[] = [
  {
    id: 'starch',
    name: 'Starch',
    monomer: 'α-glucose',
    link: 'α(1→4) glycosidic links, with α(1→6) branches in amylopectin',
    role: 'Energy store in plants; coils into helices that enzymes in our gut can digest.',
    residueCounts: GLUCOSE_RESIDUE,
  },
  {
    id: 'cellulose',
    name: 'Cellulose',
    monomer: 'β-glucose',
    link: 'β(1→4) glycosidic links',
    role: 'Plant cell walls; straight chains hydrogen-bond side by side into strong fibres that humans cannot digest.',
    residueCounts: GLUCOSE_RESIDUE,
  },
];

export const GLYCEROL: ElementCounts = { C: 3, H: 8, O: 3 };

export interface FattyAcid {
  id: string;
  name: string;
  /** Hydrocarbon chain written after –CO– in the triglyceride. */
  chain: string;
  counts: ElementCounts;
  doubleBonds: number;
  triglycerideName: string;
  /** State of the simple triglyceride at SLC (25 °C). */
  triglycerideState: 'solid fat' | 'liquid oil';
}

export const FATTY_ACIDS: FattyAcid[] = [
  {
    id: 'palmitic',
    name: 'palmitic acid (hexadecanoic acid)',
    chain: 'C₁₅H₃₁',
    counts: { C: 16, H: 32, O: 2 },
    doubleBonds: 0,
    triglycerideName: 'tripalmitin',
    triglycerideState: 'solid fat',
  },
  {
    id: 'stearic',
    name: 'stearic acid (octadecanoic acid)',
    chain: 'C₁₇H₃₅',
    counts: { C: 18, H: 36, O: 2 },
    doubleBonds: 0,
    triglycerideName: 'tristearin',
    triglycerideState: 'solid fat',
  },
  {
    id: 'oleic',
    name: 'oleic acid (cis-octadec-9-enoic acid)',
    chain: 'C₁₇H₃₃',
    counts: { C: 18, H: 34, O: 2 },
    doubleBonds: 1,
    triglycerideName: 'triolein',
    triglycerideState: 'liquid oil',
  },
  {
    id: 'linoleic',
    name: 'linoleic acid (cis,cis-octadeca-9,12-dienoic acid)',
    chain: 'C₁₇H₃₁',
    counts: { C: 18, H: 32, O: 2 },
    doubleBonds: 2,
    triglycerideName: 'trilinolein',
    triglycerideState: 'liquid oil',
  },
];

export const FATTY_ACIDS_PER_TRIGLYCERIDE = 3;

export interface Triglyceride {
  fattyAcid: FattyAcid;
  name: string;
  lines: FormulaSegment[][];
  waterReleased: number;
  counts: ElementCounts;
  formula: string;
  saturation: 'saturated' | 'unsaturated';
}

/** Glycerol + three identical fatty acids → triglyceride + 3 H₂O; null for an unknown id. */
export function buildTriglyceride(fattyAcidId: string): Triglyceride | null {
  const fattyAcid = FATTY_ACIDS.find((item) => item.id === fattyAcidId);
  if (!fattyAcid) return null;
  const carbons = ['CH₂', 'CH', 'CH₂'];
  const counts = combineCounts(
    { counts: GLYCEROL },
    { counts: fattyAcid.counts, times: FATTY_ACIDS_PER_TRIGLYCERIDE },
    { counts: WATER, times: -FATTY_ACIDS_PER_TRIGLYCERIDE },
  );
  return {
    fattyAcid,
    name: fattyAcid.triglycerideName,
    lines: carbons.map((carbon) => [
      { text: `${carbon}–` },
      { text: 'O–CO', link: true },
      { text: `–${fattyAcid.chain}` },
    ]),
    waterReleased: FATTY_ACIDS_PER_TRIGLYCERIDE,
    counts,
    formula: formatFormula(counts),
    saturation: fattyAcid.doubleBonds === 0 ? 'saturated' : 'unsaturated',
  };
}

// ---------------------------------------------------------------------------------------------
// Biomolecule question bank
// ---------------------------------------------------------------------------------------------

export type BiomoleculeTopic = 'proteins' | 'protein-structure' | 'carbohydrates' | 'lipids';

export const BIOMOLECULE_TOPICS: { id: BiomoleculeTopic; label: string }[] = [
  { id: 'proteins', label: 'Amino acids & peptides' },
  { id: 'protein-structure', label: 'Protein structure' },
  { id: 'carbohydrates', label: 'Carbohydrates' },
  { id: 'lipids', label: 'Lipids' },
];

export interface BiomoleculeQuestion extends ChoiceQuestion {
  topic: BiomoleculeTopic;
}

export const BIOMOLECULE_QUESTIONS: BiomoleculeQuestion[] = [
  {
    id: 'peptide-link',
    topic: 'proteins',
    prompt: 'Which link joins one amino acid to the next in a protein chain?',
    options: [
      {
        id: 'ester',
        label: 'Ester –COO–',
        correct: false,
        feedback:
          'An ester needs an alcohol –OH. Amino acids join through their amino group, so the link contains nitrogen: –CO–NH–.',
      },
      {
        id: 'peptide',
        label: 'Peptide (amide) –CO–NH–',
        correct: true,
        feedback:
          'Correct. The –COOH of one amino acid condenses with the –NH₂ of the next, releasing H₂O and forming a peptide (amide) bond.',
      },
      {
        id: 'glycosidic',
        label: 'Glycosidic C–O–C',
        correct: false,
        feedback:
          'Glycosidic links join sugar units in carbohydrates. Amino acids join by peptide (amide) bonds, –CO–NH–.',
      },
      {
        id: 'disulfide',
        label: 'Disulfide –S–S–',
        correct: false,
        feedback:
          'A disulfide bridge forms only between two cysteine R groups and helps hold tertiary structure. The chain itself is joined by peptide bonds.',
      },
    ],
  },
  {
    id: 'tripeptide-water',
    topic: 'proteins',
    prompt: 'Three amino acids join to form a tripeptide. How many water molecules are released?',
    options: [
      {
        id: 'one',
        label: '1',
        correct: false,
        feedback:
          'Each peptide bond releases one H₂O. Three amino acids in a row need two peptide bonds, so two H₂O are released.',
      },
      {
        id: 'two',
        label: '2',
        correct: true,
        feedback:
          'Correct. n amino acids form n − 1 peptide bonds, and each bond releases one H₂O: 3 − 1 = 2.',
      },
      {
        id: 'three',
        label: '3',
        correct: false,
        feedback:
          'The two ends of the chain stay as –NH₂ and –COOH, so only the links between neighbours release water: 3 − 1 = 2.',
      },
    ],
  },
  {
    id: 'disulfide-r-group',
    topic: 'proteins',
    prompt: 'Which amino acid has an R group that can form a disulfide bridge?',
    options: [
      {
        id: 'ser',
        label: 'Serine (–CH₂OH)',
        correct: false,
        feedback:
          'Serine’s –OH forms hydrogen bonds, not covalent bridges. A disulfide bridge needs sulfur: cysteine’s –CH₂SH.',
      },
      {
        id: 'cys',
        label: 'Cysteine (–CH₂SH)',
        correct: true,
        feedback:
          'Correct. Two cysteine –SH groups can be oxidised to a covalent –S–S– bridge that locks part of the tertiary structure.',
      },
      {
        id: 'lys',
        label: 'Lysine (–CH₂CH₂CH₂CH₂NH₂)',
        correct: false,
        feedback:
          'Lysine’s amino group can form ionic interactions as –NH₃⁺. Only cysteine has the sulfur needed for a disulfide bridge.',
      },
      {
        id: 'ala',
        label: 'Alanine (–CH₃)',
        correct: false,
        feedback:
          'Alanine’s –CH₃ takes part only in dispersion forces. A disulfide bridge needs cysteine’s –SH group.',
      },
    ],
  },
  {
    id: 'secondary-hydrogen-bonds',
    topic: 'protein-structure',
    prompt:
      'Hydrogen bonds between backbone C=O and N–H groups make α-helices and β-pleated sheets. Which level of structure is this?',
    options: [
      {
        id: 'primary',
        label: 'Primary',
        correct: false,
        feedback:
          'Primary structure is only the sequence, held by covalent peptide bonds. Backbone hydrogen bonding is secondary structure.',
      },
      {
        id: 'secondary',
        label: 'Secondary',
        correct: true,
        feedback:
          'Correct. Secondary structure comes from hydrogen bonds between backbone groups, not between R groups.',
      },
      {
        id: 'tertiary',
        label: 'Tertiary',
        correct: false,
        feedback:
          'Tertiary structure depends on R-group interactions. These hydrogen bonds are between backbone C=O and N–H, so this is secondary.',
      },
      {
        id: 'quaternary',
        label: 'Quaternary',
        correct: false,
        feedback:
          'Quaternary structure needs two or more chains. A single helix or sheet is secondary structure.',
      },
    ],
  },
  {
    id: 'quaternary-haemoglobin',
    topic: 'protein-structure',
    prompt: 'Haemoglobin is made of four polypeptide chains held together. Which level is this?',
    options: [
      {
        id: 'tertiary',
        label: 'Tertiary',
        correct: false,
        feedback:
          'Tertiary describes how one chain folds. Four chains fitting together is quaternary structure.',
      },
      {
        id: 'quaternary',
        label: 'Quaternary',
        correct: true,
        feedback:
          'Correct. Quaternary structure is the association of two or more polypeptide chains, held by R-group interactions between chains.',
      },
      {
        id: 'secondary',
        label: 'Secondary',
        correct: false,
        feedback:
          'Secondary structure is local helices and sheets within a chain. Joining four chains is quaternary.',
      },
    ],
  },
  {
    id: 'primary-bonds',
    topic: 'protein-structure',
    prompt: 'Heating a protein denatures it. Which bonds usually stay intact?',
    options: [
      {
        id: 'hydrogen',
        label: 'Hydrogen bonds',
        correct: false,
        feedback:
          'Hydrogen bonds are weak and break on heating, which unfolds the secondary and tertiary structure.',
      },
      {
        id: 'peptide',
        label: 'Peptide bonds of the primary structure',
        correct: true,
        feedback:
          'Correct. Denaturing breaks the weaker interactions, so the shape is lost, but the covalent peptide bonds and the sequence remain.',
      },
      {
        id: 'ionic',
        label: 'Ionic interactions between R groups',
        correct: false,
        feedback:
          'Ionic interactions between R groups are disrupted by heat and pH changes. The covalent peptide bonds stay.',
      },
    ],
  },
  {
    id: 'maltose-product',
    topic: 'carbohydrates',
    prompt: 'Glucose + glucose → maltose. What else is produced?',
    options: [
      {
        id: 'water',
        label: 'H₂O',
        correct: true,
        feedback:
          'Correct. This is a condensation reaction: 2 C₆H₁₂O₆ → C₁₂H₂₂O₁₁ + H₂O, forming a glycosidic link.',
      },
      {
        id: 'carbon-dioxide',
        label: 'CO₂',
        correct: false,
        feedback:
          'No carbon is lost: maltose has all 12 carbons. The atoms that leave are H and OH, as H₂O.',
      },
      {
        id: 'nothing',
        label: 'Nothing — the sugars simply add together',
        correct: false,
        feedback:
          'Check the formulae: 2 × C₆H₁₂O₆ = C₁₂H₂₄O₁₂, but maltose is C₁₂H₂₂O₁₁. The difference is one H₂O.',
      },
    ],
  },
  {
    id: 'sucrose-monomers',
    topic: 'carbohydrates',
    prompt: 'Which two monosaccharides condense to form sucrose?',
    options: [
      {
        id: 'glucose-glucose',
        label: 'Glucose + glucose',
        correct: false,
        feedback: 'Two glucose units make maltose. Sucrose is glucose joined to fructose.',
      },
      {
        id: 'glucose-fructose',
        label: 'Glucose + fructose',
        correct: true,
        feedback:
          'Correct. Glucose and fructose (both C₆H₁₂O₆) condense to sucrose, C₁₂H₂₂O₁₁, and one H₂O.',
      },
      {
        id: 'fructose-fructose',
        label: 'Fructose + fructose',
        correct: false,
        feedback:
          'Sucrose contains one glucose unit and one fructose unit, not two fructose units.',
      },
    ],
  },
  {
    id: 'starch-cellulose',
    topic: 'carbohydrates',
    prompt: 'Starch and cellulose are both polymers of glucose. What is the key difference?',
    options: [
      {
        id: 'beta-links',
        label: 'Cellulose is made from β-glucose; starch from α-glucose',
        correct: true,
        feedback:
          'Correct. β(1→4) links give cellulose straight chains that hydrogen-bond into fibres; α links let starch coil and be digested.',
      },
      {
        id: 'fructose',
        label: 'Cellulose is a polymer of fructose',
        correct: false,
        feedback:
          'Both polymers are built only from glucose. They differ in the form of glucose (α or β) and so in the link geometry.',
      },
      {
        id: 'peptide',
        label: 'Cellulose units are joined by peptide bonds',
        correct: false,
        feedback:
          'Peptide bonds join amino acids. Starch and cellulose both use glycosidic links; only the α/β form differs.',
      },
    ],
  },
  {
    id: 'triglyceride-water',
    topic: 'lipids',
    prompt:
      'Glycerol reacts with three fatty acids to form a triglyceride. What forms, and which link joins them?',
    options: [
      {
        id: 'three-ester',
        label: '3 H₂O; ester links',
        correct: true,
        feedback:
          'Correct. Each of glycerol’s three –OH groups condenses with a fatty acid –COOH, forming three ester links and three H₂O.',
      },
      {
        id: 'one-ester',
        label: '1 H₂O; ester links',
        correct: false,
        feedback:
          'One water forms for each ester link. Glycerol has three –OH groups and three fatty acids attach, so three H₂O form.',
      },
      {
        id: 'three-peptide',
        label: '3 H₂O; peptide links',
        correct: false,
        feedback:
          'Peptide links need an –NH₂ group. Glycerol supplies –OH groups, so the links are esters.',
      },
    ],
  },
  {
    id: 'unsaturated-state',
    topic: 'lipids',
    prompt:
      'Triolein is made from oleic acid, which has one cis C=C. At 25 °C, how does it compare with tristearin?',
    options: [
      {
        id: 'liquid',
        label: 'It is a liquid oil; the kinks stop close packing',
        correct: true,
        feedback:
          'Correct. A cis C=C puts a kink in each chain, so chains cannot pack closely, dispersion forces are weaker and the triglyceride is liquid.',
      },
      {
        id: 'solid',
        label: 'It is a harder solid; double bonds are stronger',
        correct: false,
        feedback:
          'Melting depends on forces between molecules, not on bond strength inside them. Kinked unsaturated chains pack poorly, so triolein is a liquid oil.',
      },
      {
        id: 'same',
        label: 'Same state; both have 18-carbon chains',
        correct: false,
        feedback:
          'Chain length is the same, but shape differs. Straight saturated chains pack closely (solid fat); kinked unsaturated chains do not (liquid oil).',
      },
    ],
  },
  {
    id: 'saturated-meaning',
    topic: 'lipids',
    prompt: 'What makes a fatty acid “saturated”?',
    options: [
      {
        id: 'no-double',
        label: 'Its hydrocarbon chain has no C=C double bonds',
        correct: true,
        feedback:
          'Correct. Every chain carbon holds as many hydrogens as possible. Stearic acid, C₁₇H₃₅COOH, is saturated.',
      },
      {
        id: 'water',
        label: 'It has dissolved as much as possible in water',
        correct: false,
        feedback:
          'Here “saturated” is about bonding, not solubility. A saturated fatty acid has only C–C single bonds in its chain.',
      },
      {
        id: 'three',
        label: 'It is attached to all three glycerol –OH groups',
        correct: false,
        feedback:
          'That describes a triglyceride. Saturation describes the fatty-acid chain: no C=C double bonds.',
      },
    ],
  },
];
